import { useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  Eye,
  EyeOff,
  Zap,
  ArrowRight,
  Sparkles,
  GraduationCap,
  Globe,
} from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/logo/logo.jpeg";

const highlights = [
  {
    icon: <GraduationCap className="w-4 h-4" />,
    text: "300+ partner universities",
  },
  { icon: <Sparkles className="w-4 h-4" />, text: "Up to 100% scholarships" },
  { icon: <Globe className="w-4 h-4" />, text: "40+ countries covered" },
];

export default function LoginPage() {
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    setMessage("");
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      navigate("/dashboard");
    } else {
      setError("Invalid email or password");
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setMessage("");
      setError("Please enter your email address first.");
      return;
    }

    setError("");
    setMessage("");
    setResetLoading(true);
    const ok = await resetPassword(trimmedEmail);
    setResetLoading(false);

    if (ok) {
      setMessage("Password reset email sent. Please check your inbox.");
    } else {
      setError(
        "Unable to send password reset email. Please check the email address.",
      );
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Left — Brand panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        {/* BG blobs */}
        <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-violet-800/10 blur-3xl pointer-events-none" />

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <img src={logo} alt="scs education logo" className="w-12 h-12" />
        </Link>

        {/* Center content */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-[48px] text-white mb-6 leading-tight tracking-tight"
              style={{ fontWeight: 900 }}
            >
              Welcome{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #A78BFA, #67E8F9)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                back.
              </span>
            </h1>
            <p
              className="text-[16px] text-slate-400 mb-10 leading-relaxed max-w-sm"
              style={{ fontWeight: 400 }}
            >
              Your dream degree is closer than you think. Sign in to track your
              applications and connect with your counselor.
            </p>

            <div className="flex flex-col gap-4">
              {highlights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400">
                    {h.icon}
                  </div>
                  <span
                    className="text-[14px] text-slate-300"
                    style={{ fontWeight: 500 }}
                  >
                    {h.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10">
          <p
            className="text-[13px] text-slate-500 italic"
            style={{ fontWeight: 400 }}
          >
            "SCS Education changed my life. I got a full scholarship to Taylor's
            in just 3 months."
          </p>
          <p
            className="text-[12px] text-slate-600 mt-2"
            style={{ fontWeight: 600 }}
          >
            — Ain Nabila, MBBS Student 🎓
          </p>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#FAFAFA]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <Link to="/" className="flex justify-center mb-10 lg:hidden">
            <img src={logo} alt="" className="w-14 h-14" />
          </Link>

          <div className="mb-8">
            <h2
              className="text-[32px] text-slate-900 tracking-tight mb-2"
              style={{ fontWeight: 800 }}
            >
              Sign in
            </h2>
            <p
              className="text-[14px] text-slate-500"
              style={{ fontWeight: 400 }}
            >
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-violet-600 hover:text-violet-700 transition-colors"
                style={{ fontWeight: 600 }}
              >
                Create one free
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Email */}
            <div>
              <label
                className="block text-[13px] text-slate-700 mb-1.5"
                style={{ fontWeight: 600 }}
              >
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-800 text-[14px] outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all placeholder-slate-400"
                style={{ fontWeight: 400 }}
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  className="text-[13px] text-slate-700"
                  style={{ fontWeight: 600 }}
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  className="text-[12px] text-violet-600 hover:text-violet-700"
                  style={{ fontWeight: 500 }}
                >
                  {resetLoading ? "Sending..." : "Forgot password?"}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-12 rounded-2xl border border-slate-200 bg-white text-slate-800 text-[14px] outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all placeholder-slate-400"
                  style={{ fontWeight: 400 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p
                className="text-[13px] text-red-500 bg-red-50 px-4 py-2.5 rounded-xl"
                style={{ fontWeight: 500 }}
              >
                {error}
              </p>
            )}
            {message && (
              <p
                className="text-[13px] text-green-600 bg-green-50 px-4 py-2.5 rounded-xl"
                style={{ fontWeight: 500 }}
              >
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              style={{ fontWeight: 600 }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign in <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p
            className="text-center text-[12px] text-slate-400 mt-8"
            style={{ fontWeight: 400 }}
          >
            By signing in, you agree to our{" "}
            <span className="text-slate-600 cursor-pointer hover:underline">
              Terms of Service
            </span>{" "}
            and{" "}
            <span className="text-slate-600 cursor-pointer hover:underline">
              Privacy Policy
            </span>
            .
          </p>
        </motion.div>
      </div>
    </div>
  );
}
